import { call, put, takeLatest, select } from 'redux-saga/effects';
import { cloneDeep, get, set, isEqual, isNull, findIndex, merge } from 'lodash';
import history from 'browserHistory';
import { objToFormData, trims } from 'utils';
import { setTab, deleteTab } from 'modules/common/Tabs/actions';
import { Document } from '../../../entities/Document';
import { PhoneService } from '../../../entities/Phone/service';
import { TripService, NewTrip, Trip, tripStatuses } from '../../../entities/Trip';
import { cleanTripFields } from '../FillTrip';
import {
  GET_NEW_TRIP_REQUEST,
  UPDATE_TRIP_REQUEST,
  DELETE_TRIP_REQUEST,
  GET_TRIP_REQUEST,
  UPDATE_TRIP_STATUS,
  PUT_NEXT_STEP_REQUEST,
} from './constants';
import { commonTripSelector, dataDocsSelector, visitorSelector } from './selectors';
import {
  getTripRequest,
  getNewTripSuccess,
  getNewTripError,
  updateTripError,
  deleteTripRequest,
  deleteTripError,
  cleanTrip,
  updateTripSuccess,
  setVisitorName,
  getNewTripRequest,
  updateTripStatusSuccess,
  updateTripRequest,
  putNextStepRequest,
  putNextStepSuccess,
  putNextStepError,
} from './actions';

function generateTab(id: number) {
  const tabId = `trip_${id}`;
  const path = history.location.pathname.split('/')[1];

  return {
    title: `Peйс ${id}`,
    href: `/${path}/trip/${id}`,
    id: tabId,
    closeRedirectPath: `/${path}`,
  };
}

export const getDocs = (trip: Trip) => {
  const driverDocs = cloneDeep(get(trip, 'driver.documents', []));
  const forwarderDocs = cloneDeep(get(trip, 'forwarder.documents', []));
  const vehicleDocs = cloneDeep(get(trip, 'vehicle.document', {}));
  const trailerDocs = cloneDeep(get(trip, 'trailer.document', {}));

  driverDocs?.forEach((doc: any) => set(doc, 'fieldName', 'driver'));
  forwarderDocs?.forEach((doc: any) => set(doc, 'fieldName', 'forwarder'));
  set(vehicleDocs, 'fieldName', 'vehicle');
  set(trailerDocs, 'fieldName', 'trailer');

  return [...driverDocs, ...forwarderDocs, vehicleDocs, trailerDocs] as Document[];
};

export const getVisitor = (trip: Trip) => {
  const driver = {
    fieldName: 'driver',
    firstName: cloneDeep(get(trip, 'driver.firstName', '')),
    middleName: cloneDeep(get(trip, 'driver.middleName', '')),
    lastName: cloneDeep(get(trip, 'driver.lastName', '')),
    phone: cloneDeep(get(trip, 'driver.phone', { id: '', number: '' })),
    id: cloneDeep(get(trip, 'driver.id', '')),
  };

  const forwarder = {
    fieldName: 'forwarder',
    firstName: cloneDeep(get(trip, 'forwarder.firstName', '')),
    middleName: cloneDeep(get(trip, 'forwarder.middleName', '')),
    lastName: cloneDeep(get(trip, 'forwarder.lastName', '')),
    phone: cloneDeep(get(trip, 'forwarder.phone', { id: '', number: '' })),
    id: cloneDeep(get(trip, 'forwarder.id', '')),
  };

  const vehicle = {
    fieldName: 'vehicle',
    licensePlate: cloneDeep(get(trip, 'vehicle.licensePlate', '')),
    id: cloneDeep(get(trip, 'vehicle.id', '')),
  };

  const trailer = {
    fieldName: 'trailer',
    licensePlate: cloneDeep(get(trip, 'trailer.licensePlate', '')),
    id: cloneDeep(get(trip, 'trailer.id', '')),
  };

  return [driver, forwarder, vehicle, trailer];
};

export function prepareData(data: any, visitorBase: any, docsBase: any) {
  const role = history.location.pathname.split('/')[1];

  // @ts-ignore
  const depTitle = JSON.parse(localStorage.getItem('depTitle') || 'null')?.id;

  if ((+data.statusId === 2 || +data.statusId === 10) && (role === 'security' || role === 'kiosk')) {
    set(data, 'arrivalSecurityGateId', depTitle);
  }
  if ((+data.statusId === 99 && role === 'security') || +data.statusId === 8) {
    set(data, 'departureSecurityGateId', depTitle);
  }

  ['vehicle', 'trailer'].forEach((field) => {
    const licensePlate = get(data, `${field}.licensePlate`, '');
    const formsDocs = get(data, `${field}.document`, {});

    const baseVisitor = visitorBase.find((v: any) => v?.fieldName === field);
    const baseID = get(baseVisitor, 'id', '');
    const licensePlateBase = get(baseVisitor, 'licensePlate', '');
    const baseDocs = docsBase.find((v: any) => v?.fieldName === field);

    const number = get(data, `${field}.document.number`, '');
    const baseNumber = get(baseDocs, 'number');

    const baseDocID = get(baseDocs, 'id', '');

    if ((!licensePlate || !licensePlate.trim().length) && !formsDocs?.number) {
      // eslint-disable-next-line
      return delete data[field];
    }

    if (!isEqual(licensePlate, licensePlateBase)) {
      set(data, `${field}.id`, '0');
    } else {
      set(data, `${field}.id`, baseID);
    }

    if (!isEqual(trims(String(number)), trims(String(baseNumber)))) {
      set(data, `${field}.document.id`, '0');
    } else {
      set(data, `${field}.document.id`, baseDocID);
    }
    // eslint-disable-next-line
    if (!!formsDocs.fieldName) delete formsDocs.fieldName;
  });
  ['driver', 'forwarder'].forEach((field) => {
    const dataFileld = get(data, `${field}`);

    // eslint-disable-next-line
    if (isNull(dataFileld)) return delete data[field];

    const phone = get(data, `${field}.phone.number`, '');

    const formDocs = get(data, `${field}.documents`, []);
    const numberDoc1 = formDocs.find((v: any) => isEqual(v.type, 1))?.number || '';
    const numberDoc3 = formDocs.find((v: any) => isEqual(v.type, 3))?.number || '';

    const isFaceId = get(data, `${field}.isFaceId`);

    const baseVisitor = visitorBase.find((v: any) => v.fieldName === field);
    const baseID = get(baseVisitor, 'id');
    const basePhone = get(baseVisitor, 'phone.number');

    const baseDoc1 = docsBase.find((v: any) => isEqual(v?.fieldName, field) && isEqual(v?.type, 1)) || {};
    const baseDoc3 = docsBase.find((v: any) => isEqual(v?.fieldName, field) && isEqual(v?.type, 3)) || {};

    const baseDocsID1 = baseDoc1?.id || '';
    const baseDocsID3 = baseDoc3?.id || '';
    const numberBaseDoc1 = baseDoc1.number || '';
    const numberBaseDoc3 = baseDoc3?.number || '';

    const picturesDoc3 = [...get(formDocs, 'pictures', []), ...get(formDocs, 'pictureFiles', [])];

    // eslint-disable-next-line
    if (!picturesDoc3.length && !numberDoc3.length && isEqual(field, 'forwarder')) return delete data[field].documents;

    const fieldObj = {
      firstName: get(data, `${field}.firstName`, ''),
      middlName: get(data, `${field}.middleName`, ''),
      lastName: get(data, `${field}.lastName`, ''),
    };
    const databasedObj = {
      firstName: get(baseVisitor, 'firstName', ''),
      middlName: get(baseVisitor, 'middleName', ''),
      lastName: get(baseVisitor, 'lastName', ''),
    };

    const isOneTypeDocs = !!Object.keys(baseDoc1).length;

    if ((baseID && isEqual(fieldObj, databasedObj)) || isFaceId) set(data, `${field}.id`, baseID);
    if (baseID && !isEqual(fieldObj, databasedObj) && !isFaceId) set(data, `${field}.id`, '0');

    const idxDoc1 = findIndex(data[field].documents, (d: any) => d.number === numberBaseDoc1);
    const idxDoc3 = findIndex(data[field].documents, (d: any) => d.number === numberBaseDoc3);

    if (isEqual(numberDoc1, numberBaseDoc1)) set(data, `${field}.documents[${idxDoc1}].id`, baseDocsID1);
    if (!isEqual(numberDoc1, numberBaseDoc1)) set(data, `${field}.documents[${idxDoc1}].id`, '0');

    if (isEqual(numberDoc3, numberBaseDoc3))
      set(data, `${field}.documents[${!isOneTypeDocs ? '0' : idxDoc3}].id`, baseDocsID3);
    if (numberBaseDoc3.length && !isEqual(numberDoc3, numberBaseDoc3))
      set(data, `${field}.documents[${!isOneTypeDocs ? '0' : idxDoc3}].id`, '0');

    if (phone) {
      const val = PhoneService.unformatPhone(phone);
      if (isEqual(val.length, 12)) {
        set(data, `${field}.phone.number`, val);
      } else {
        set(data, `${[field]}.phone`, null);
      }

      if (basePhone && isEqual(val.length, 12) && !isEqual(val, basePhone)) {
        set(data, `${field}.phone.id`, '0');
      }
    } else {
      set(data, `${[field]}.phone`, null);
    }
  });

  // eslint-disable-next-line
  delete data.status;
  // eslint-disable-next-line
  if (!data.forwarder?.firstName && !data.forwarder?.lastName) delete data.forwarder.isFaceId;

  return data;
}

function* getNewTripSaga(action: ReturnType<typeof getNewTripRequest>) {
  try {
    const Intention = action.payload;
    const branchLocal = localStorage.getItem('branchId') || '';
    const branchID = JSON.parse(branchLocal)?.id;
    const data = {
      branchId: branchID,
      tripIntention: Intention,
    };
    const trip: NewTrip = yield call(TripService.getNewTrip, objToFormData(data));
    const { id } = trip;
    const path = history.location.pathname.split('/')[1];

    history.push(`/${path}/trip/${id}`);
    // @ts-ignore
    const docs = getDocs(trip);
    // @ts-ignore
    yield put(getNewTripSuccess(trip, docs));
    // @ts-ignore
    yield put(setVisitorName(getVisitor(trip)));
    yield put(setTab(generateTab(id)));
  } catch (error: any) {
    yield put(getNewTripError(error));
  }
}

function* getTripSaga(action: ReturnType<typeof getTripRequest>) {
  const { payload } = action;
  try {
    const recievedTrip: Trip = yield call(TripService.getTrip, payload);
    const { trip } = yield select(commonTripSelector);
    const mergedTrip = merge(trip, recievedTrip);
    const { status, id } = mergedTrip;
    if (status.id === tripStatuses.waitingDeparture) {
      mergedTrip.departureVehicleWeight = 0;
    }

    const docs = getDocs(mergedTrip);

    yield put(getNewTripSuccess({ ...mergedTrip }, docs));
    yield put(setVisitorName(getVisitor({ ...mergedTrip })));
    yield put(setTab(generateTab(id)));
  } catch (error: any) {
    yield put(getNewTripError(error));
  }
}

function* updateTripSaga(action: ReturnType<typeof updateTripRequest>) {
  try {
    const { userStatusId, statusId } = action;
    const { isSave } = action.payload;

    // @ts-ignore
    const { trip } = yield select(commonTripSelector);
    const { id } = trip;

    // @ts-ignore
    const docsBase = yield select(dataDocsSelector);
    // @ts-ignore
    const visitorBase = yield select(visitorSelector);

    const statuses: number[] = Array.isArray(statusId) ? statusId : [trip.statusId];

    const data = cloneDeep(trip);

    // eslint-disable-next-line no-restricted-syntax
    for (const nextStatusId of statuses) {
      data.userStatus = userStatusId;
      data.statusId = nextStatusId;

      yield call(prepareData, data, visitorBase, docsBase);

      if (isSave) {
        yield call(TripService.saveTrip, id, objToFormData(data));
      } else {
        yield call(TripService.updateTrip, id, objToFormData(data));
      }

      yield put(updateTripSuccess());

      yield put(cleanTripFields());
    }
  } catch (error: any) {
    yield put(updateTripError(error.errorExtraParams));
  }
}

// function* updateTripSaga(action: ReturnType<typeof updateTripTwoStageRequest>) {
//   try {
//     const { statusId } = action;
//     const { isSave } = action.payload;

//     // @ts-ignore
//     const { trip } = yield select(commonTripSelector);

//     const { id } = trip;
//     const data = cloneDeep(trip);
//     data.userStatus = statusId;

//     // @ts-ignore
//     const docsBase = yield select(dataDocsSelector);
//     // @ts-ignore
//     const visitorBase = yield select(visitorSelector);

//     yield call(prepareData, data, visitorBase, docsBase);

//     if (isSave) {
//       yield call(TripService.saveTrip, id, objToFormData(data));
//     } else {
//       yield call(TripService.updateTrip, id, objToFormData(data));
//     }
//     yield put(updateTripSuccess());

//     yield put(cleanTripFields());
//   } catch (error: any) {
//     yield put(updateTripError(error.errorExtraParams));
//   }
// }

function* updateTripStatusSaga() {
  try {
    // @ts-ignore
    const { trip } = yield select(commonTripSelector);
    const { id, statusId } = trip;
    let prevStatus;
    switch (statusId) {
      case 8:
        prevStatus = 6;
        break;
      case 6:
        prevStatus = 2;
        break;
      default:
        // eslint-disable-next-line no-unused-expressions
        prevStatus = 0;
    }

    const data = {
      tripId: id,
      status: prevStatus,
    };
    yield call(TripService.updateTripStatus, objToFormData(data));
    yield put(updateTripStatusSuccess());
  } catch (error: any) {
    yield put(updateTripError(Error(error?.message || 'Виникла помилка')));
  }
}

function* deleteTripSaga(action: ReturnType<typeof deleteTripRequest>) {
  try {
    const { payload } = action;
    yield call(TripService.deleteTrip, payload.tripId);
    const path = history.location.pathname.split('/')[1];

    if (payload.tripIntention === 2) {
      history.push(`/${path}/unload`);
    } else {
      history.push(`/${path}`);
    }
    yield put(deleteTab(`trip_${payload.tripId}`));
    yield put(cleanTrip());
    yield put(cleanTripFields());
  } catch (error: any) {
    yield put(deleteTripError(error));
  }
}

function* putNextStepSaga(action: ReturnType<typeof putNextStepRequest>) {
  try {
    const { payload } = action;
    payload.statusId = 2;
    yield call(TripService.nextStep, objToFormData(payload));

    yield put(putNextStepSuccess());
  } catch (error: any) {
    yield put(putNextStepError(error));
  }
}

export function* commonTripSagaWatcher() {
  yield takeLatest(GET_NEW_TRIP_REQUEST, getNewTripSaga);
  yield takeLatest(GET_TRIP_REQUEST, getTripSaga);
  yield takeLatest(UPDATE_TRIP_REQUEST, updateTripSaga);
  yield takeLatest(UPDATE_TRIP_STATUS, updateTripStatusSaga);
  yield takeLatest(PUT_NEXT_STEP_REQUEST, putNextStepSaga);
  yield takeLatest(DELETE_TRIP_REQUEST, deleteTripSaga);
}
